<template>
    <q-stepper
        v-model="step"
        ref="stepper"
        contracted
        color="primary"
        animated
    >
        <q-step
            :name="1"
            title="Currency"
            icon="fa fa-money-bill-alt"
            :done="step > 1"
        >
            <div class="row">
                <div class="col-12 flex flex-center">
                    <p class="text-h6">{{onRecipientSide ? $t('recipients.select_recipient_currency') : $t('recipients.confirm_recipient_currency')}}:</p>
                </div>
                <div class="col-12 row flex flex-center" dir="ltr">
                    <q-select
                        outlined
                        v-model="RecipientObj.currency_acronym"
                        :options="outgoing_currencies"
                        option-value="acronym"
                        option-label="name"
                        map-options
                        emit-value
                        color="primary"
                        style="width:250px;"
                        v-if="onRecipientSide"
                    >
                        <template v-slot:prepend>
                            <q-icon :name="currency_icon"/>
                        </template>
                    </q-select>

                    <q-input
                        outlined
                        :value="outgoing_currency_name"
                        readonly
                        style="width:250px;"
                        v-else
                    >
                        <template v-slot:prepend>
                            <q-icon :name="currency_icon"/>
                        </template>
                    </q-input>
                </div> <!-- CURRENCY ACRONYM -->

                <div class="q-mt-md" v-if="!onRecipientSide">
                    <q-icon name="fa fa-exclamation-circle" color="blue" size="xs"/>&nbsp;
                    <span v-html="$t('recipients.notice1', {currency: outgoing_currency ? outgoing_currency.symbol : ''})"></span>
                </div>
            </div>

            <q-stepper-navigation class="flex justify-between">
                <q-btn
                    flat
                    color="primary"
                    :label="$t('cancel')"
                    :disabled="loading"
                    v-close-popup
                />
                <q-btn @click="gotoPersonal" color="primary" :label="$t('continue')" class="q-ml-sm"
                       :disabled="! RecipientObj.currency_acronym"/>
            </q-stepper-navigation>
        </q-step>

        <q-step
            :name="2"
            title="Recipient info"
            icon="fa fa-user"
            :done="step > 2"
        >
            <div class="row q-col-gutter-sm">
                <div class="col-12 flex flex-center text-h6">
                    {{$t('recipients.details_for', {country: outgoing_country ? outgoing_country.name : ''})}}
                </div>
                <div class="col-12" v-if="userSendFromOwnCountry || ['TX'].includes(outgoing_country ? outgoing_country.acronym : '')">
                    <q-checkbox
                        size="sm"
                        v-model="sendToMyOwn"
                        :val="true"
                        color="primary"
                        @input="toggleRecipientBeUser"
                    >
                        <div
                            v-html="$t('recipients.tick_if_recipients_account_name_for', {outgoing_country: outgoing_country ? outgoing_country.name : '', full_name: RecipientObj.user ? RecipientObj.user.name : full_name})"
                            dir='rtl'
                        >
                        </div>
                    </q-checkbox>
                </div>
                <div class="col-12">
                    <q-input
                        v-model="RecipientObj.name"
                        :label="$t('recipients.table_titles.name')"
                        lazy-rules
                        :error="RecipientObj.errors.has('name')"
                        :error-message="RecipientObj.errors.get('name')"
                        :rules="[ val => val && val.length > 0 || $t('validations.is_required', {field_name: $t('recipients.table_titles.name')})]"
                        color="primary"
                    >
                        <template v-slot:hint v-if="outgoing_country">
                            {{$t('recipients.full_name_of_account_holder')}} <span v-if="!['TX'].includes(outgoing_country.acronym)">{{$t('recipients.ideally_in_the_language_of')}} <q-icon :name="outgoing_country.acronym" size="xs"/> {{outgoing_country ? outgoing_country.name : ''}}</span>
                        </template>
                    </q-input>
                </div> <!-- FULLNAME -->

                <div class="col-12 q-mt-md">
                    <q-input
                        v-model="RecipientObj.address"
                        :label="$t('recipients.table_titles.address')"
                        lazy-rules
                        :error="RecipientObj.errors.has('address')"
                        :error-message="RecipientObj.errors.get('address')"
                        color="primary"
                        :hint="$t('recipients.recipient_address_hint')"
                    />
                </div> <!-- ADDRESS -->

                <div class="col col-xs-12 col-md-5 col-lg-5" dir="ltr">
                    <q-input
                        v-model="RecipientObj.phone"
                        :label="$t('recipients.table_titles.phone')"
                        lazy-rules
                        :error="RecipientObj.errors.has('phone')"
                        :error-message="RecipientObj.errors.get('phone')"
                        :hint="$t('recipients.recipients_phone_number') + ' (+'+ calling_code + ')'"
                        color="primary"
                    />
                </div> <!-- PHONE -->

                <div class="col col-xs-12 col-md-7 col-lg-7">
                    <q-input
                        v-model="RecipientObj.description"
                        :label="$t('recipients.table_titles.description') + ' ('+ $t('optional') +')'"
                        lazy-rules
                        :error="RecipientObj.errors.has('description')"
                        :error-message="RecipientObj.errors.get('description')"
                        :hint="$t('recipients.a_nickname_to_identify_this_recipient_in_your_list')"
                        color="primary"
                        maxlength="20"
                        counter
                    />
                </div> <!-- DESCRIPTION -->
            </div>

            <q-stepper-navigation class="flex justify-between">
                <div>
                    <q-btn
                        flat
                        color="primary"
                        :label="$t('cancel')"
                        :disabled="loading"
                        v-close-popup
                    />
                </div>
                <div>
                    <q-btn
                        v-if="step > 1"
                        flat
                        color="primary"
                        @click="$refs.stepper.previous()"
                        :label="$t('back')"
                        :disabled="loading"
                    />
                    <q-btn
                        @click="gotoBankAccount"
                        color="primary"
                        :label="$t('continue')"
                        :disabled="! RecipientObj.name"
                        :loading="loading"
                        class="q-ml-sm"
                    />
                </div>
            </q-stepper-navigation>
        </q-step>

        <q-step
            :name="3"
            title="Bank account"
            icon="fa fa-landmark"
        >
            <div class="row">
                <div class="col-12 flex flex-center">
                    <p class="text-h6">{{$t('recipients.how_does_your_receiver_want', {currency: outgoing_currency ? outgoing_currency.name: ''})}}</p>
                </div>
            </div>
            <recipient-bank-account-form
                v-model="RecipientObj"
                :currency-acronym="RecipientObj.currency_acronym"
            />

            <q-stepper-navigation class="flex justify-between">
                <div>
                    <q-btn
                        flat
                        color="primary"
                        :label="$t('cancel')"
                        :disabled="loading"
                        v-close-popup
                    />
                </div>
                <div>
                    <q-btn v-if="step > 1" flat color="primary" @click="$refs.stepper.previous()" :label="$t('back')"/>
                    <q-btn
                        @click="submit"
                        color="primary"
                        :label="$t('save')"
                        class="q-ml-sm"
                        :loading="saving"
                        :disabled="! isRecipientAccountFilled"
                    />
                </div>
            </q-stepper-navigation>
        </q-step>
    </q-stepper>
</template>
<script>

import RecipientBankAccountForm from "./RecipientBankAccountForm"
import Bank from "Modules/Bank/resources/models/Bank"
import {mapGetters} from 'vuex'

export default {
    name: "RecipientForm",
    model: {
        prop: 'RecipientObj',
        event: 'change'
    },
    props: {
        RecipientObj: {
            type: Object,
            required: true
        },
        saving: {
            type: Boolean,
            required: false
        }
    },
    computed: {
        ...mapGetters(['outgoing_currencies', 'currentUser']),
        full_name() {
            return this.currentUser.first_name + ' ' + (this.currentUser.middle_name ? this.currentUser.middle_name + ' ' : '') + this.currentUser.last_name
        },
        user_address() {
            return this.currentUser.address
        },
        outgoing_currency() {
            return this.outgoing_currencies.find(item => item.acronym === this.RecipientObj.currency_acronym)
        },
        outgoing_currency_name() {
            return this.outgoing_currency ? this.outgoing_currency.name : ''
        },
        outgoing_country() {
            return this.outgoing_currency ? this.outgoing_currency.country : null
        },
        calling_code() {
            return this.outgoing_country ? this.outgoing_country.calling_code : ''
        },
        currency_icon() {
            return this.outgoing_country ? this.outgoing_country.acronym : ''
        },
        isRecipientAccountFilled() {
            return this.RecipientObj.payment_method_id &&
                this.RecipientObj.number &&
                (this.RecipientObj.bank_required ? this.RecipientObj.bank_id : true)
        },
        userSendFromOwnCountry() {
            return this.outgoing_country && (this.outgoing_country.acronym === this.currentUser.country.acronym)
        },
        onRecipientSide() {
            return this.$route.name === 'recipients.my'
        }
    },
    data() {
        return {
            step: 1,
            loading: false,
            banks: [],
            banksOptions: [],
            sendToMyOwn: false
        }
    },
    methods: {
        gotoPersonal() {
            this.$refs.stepper.next()
        },
        toggleRecipientBeUser(val) {
            if (val) {
                this.RecipientObj.name = this.RecipientObj.user ? this.RecipientObj.user.name : this.full_name
                this.RecipientObj.address = this.RecipientObj.user ? this.RecipientObj.user.address : this.user_address
            } else {
                this.RecipientObj.name = ''
                this.RecipientObj.address = ''
            }
        },
        gotoBankAccount() {
            this.RecipientObj.payment_method_id = undefined
            this.RecipientObj.bank_id = undefined
            this.$refs.stepper.next()
        },
        fetchBanks() {
            this.loading = true
            Bank.list(this.RecipientObj.currency_acronym, (data) => {
                this.banks = data
                this.loading = false
                this.$refs.stepper.next()
            }).catch(error => {
                this.$q.notify({
                    message: error.data.error,
                    color: 'negative',
                    icon: 'fa fa-exclamation-triangle'
                })
                this.loading = false
            })
        },
        filterFn(val, update) {
            if (val === '') {
                update(() => {
                    this.banksOptions = this.banks
                })
                return
            }

            update(() => {
                const needle = val.toLowerCase()
                this.banksOptions = this.banks.filter(v => v.name.toLowerCase().indexOf(needle) > -1)
            })
        },
        submit() {
            this.$emit('submit')
        },
    },
    mounted() {

    },
    components: {
        RecipientBankAccountForm
    },
}
</script>
<style>

</style>
